import type { Option } from '@branchjs/neo-components';

import type {
  ILinkData,
  IQuickLink,
  IQuickLinkAnalyticsTags,
  IQuickLinkRedirects,
  ISocialMediaTags,
  QrCodeSettings,
} from '../../../../../shared/types';
import { RedirectsClientOverride } from '../../../../../shared/types';
import type { QuickLinkCreatorReducerType } from '../../reducers/QuickLinkCreatorReducer/types/QuickLinkCreatorReducerType';
import { Redirect as RedirectEnum } from '../../screens/ScreenLinkHubQuickLinkCreator/steps/RedirectsStep/Redirects.enums';
import type { Redirect as RedirectType } from '../../screens/ScreenLinkHubQuickLinkCreator/steps/RedirectsStep/Redirects.types';

export class QuickLinkCreatorService {
  private static getDomain = (domain: string): string => {
    if (domain.includes('/e')) {
      return domain.replace('/e', '');
    }

    return domain;
  };

  protected static getTitle = (quickLink: QuickLinkCreatorReducerType): string | undefined => {
    return quickLink.title;
  };

  private static getAlias = (quickLink: QuickLinkCreatorReducerType): string | undefined => {
    return quickLink.alias;
  };

  private static getTags = (tags: Option[]): string[] => {
    const parsedTags: string[] = [];

    if (tags) {
      tags.forEach((tag: Option) => {
        parsedTags.push(tag.value);
      });
    }

    return parsedTags;
  };

  protected static getAnalyticsTags = (quickLink: QuickLinkCreatorReducerType): IQuickLinkAnalyticsTags => {
    const analyticsTags = new Object() as IQuickLinkAnalyticsTags;

    if (quickLink.feature) {
      analyticsTags.feature = quickLink.feature.value;
    }

    if (quickLink.channel) {
      analyticsTags.channel = quickLink.channel.value;
    }

    if (quickLink.campaign) {
      analyticsTags.campaign = quickLink.campaign.value;
    }

    const tags = this.getTags(quickLink.tags as Option[]);
    if (tags.length > 0) {
      analyticsTags.tags = tags;
    }

    return analyticsTags;
  };

  private static getRedirectValidType = (redirect: RedirectType): RedirectsClientOverride => {
    if (redirect === RedirectEnum.Deepview) {
      return RedirectsClientOverride.Deepview;
    } else if (redirect === RedirectEnum.Web) {
      return RedirectsClientOverride.WebUrl;
    } else {
      return RedirectsClientOverride.DefaultRedirect;
    }
  };

  private static getRedirectValue = (redirect: string | Option): string => {
    if (typeof redirect === 'object') {
      return redirect.value;
    } else {
      return redirect;
    }
  };

  protected static getRedirects = (quickLink: QuickLinkCreatorReducerType): IQuickLinkRedirects => {
    const redirects: IQuickLinkRedirects = new Object();

    if (quickLink.ios) {
      redirects.ios = {
        override: this.getRedirectValidType(quickLink.iosRedirect),
        value: quickLink.ios ? this.getRedirectValue(quickLink.ios) : '',
      };
    }

    if (quickLink.android) {
      redirects.android = {
        override: this.getRedirectValidType(quickLink.androidRedirect),
        value: quickLink.android ? this.getRedirectValue(quickLink.android) : '',
      };
    }

    if (quickLink.desktop) {
      redirects.desktop = {
        override: this.getRedirectValidType(quickLink.desktopRedirect),
        value: quickLink.desktop ? this.getRedirectValue(quickLink.desktop) : '',
      };
    }

    if (quickLink.webOnlyLink) {
      redirects.isWebOnly = true;
    }

    return redirects;
  };

  protected static getLinkData = (quickLink: QuickLinkCreatorReducerType): ILinkData => {
    const linkData = new Object();

    quickLink.linkData?.forEach((entry) => {
      if (entry?.key && entry?.value) {
        linkData[entry?.key?.value as string] = typeof entry.value === 'string' ? entry.value.trim() : entry.value;
      }
    });

    return linkData;
  };

  private static getSocialMediaTags = (quickLink: QuickLinkCreatorReducerType): ISocialMediaTags => {
    const socialMediaTags: ISocialMediaTags = new Object();

    if (quickLink.socialTitle) {
      socialMediaTags.title = quickLink.socialTitle;
    }

    if (quickLink.socialDescription) {
      socialMediaTags.description = quickLink.socialDescription;
    }

    if (quickLink.socialImage) {
      socialMediaTags.imageUrl = quickLink.socialImage;
    }

    return socialMediaTags;
  };

  private static getQrCodeSettings = (quickLink: QuickLinkCreatorReducerType): QrCodeSettings | undefined => {
    return quickLink?.qrCodeSettings;
  };

  public static getQuickLinkPayload = (domain: string, quickLink: QuickLinkCreatorReducerType): IQuickLink => {
    return {
      domain: this.getDomain(domain),
      title: this.getTitle(quickLink) as string,
      alias: this.getAlias(quickLink),
      analyticsTags: this.getAnalyticsTags(quickLink),
      redirects: this.getRedirects(quickLink),
      linkData: this.getLinkData(quickLink),
      socialMediaTags: this.getSocialMediaTags(quickLink),
      qrCodeSettings: this.getQrCodeSettings(quickLink),
    };
  };
}
